import React, { useState, useMemo } from 'react';
import { Search, X, Dumbbell } from 'lucide-react';

const FOCUS_PILLS = [
  { value: 'all', label: 'All' },
  { value: 'push', label: 'Push' },
  { value: 'pull', label: 'Pull' },
  { value: 'upper', label: 'Upper' },
  { value: 'lower', label: 'Lower' },
  { value: 'full', label: 'Full Body' },
];

const ROLE_DOT = {
  main:      'bg-white',
  secondary: 'bg-blue-400',
  accessory: 'bg-gray-400',
  isolation: 'bg-gray-600',
};

// Derive a focus label from template name + exercise categories
// Works for templates that don't have an explicit focus field
const deriveTemplateFocus = (template) => {
  // 1. Explicit focus field (templates created via Guided Builder)
  if (template.focus) return template.focus.toLowerCase();
  
  const name = (template.name || template.template_name || '').toLowerCase();
  
  // 2. Match from template name
  if (/\b(push)\b/.test(name)) return 'push';
  if (/\b(pull)\b/.test(name)) return 'pull';
  if (/\b(full\s*body|fullbody|full)\b/.test(name)) return 'full';
  if (/\b(upper)\b/.test(name)) return 'upper';
  if (/\b(lower|leg|pierna)\b/.test(name)) return 'lower';
  if (/\b(pecho|chest|bench)\b/.test(name)) return 'push';
  if (/\b(espalda|back|row|pull)\b/.test(name)) return 'pull';
  if (/\b(hombro|shoulder)\b/.test(name)) return 'push';
  
  // 3. Derive from exercise names in the template
  const exercises = template.exercises || [];
  if (exercises.length === 0) return null;
  
  const names = exercises.map(ex => (ex.exercise_name || '').toLowerCase());
  const allNames = names.join(' ');
  
  // Count body-region signals
  const upperSignals = ['press', 'bench', 'fly', 'curl', 'tricep', 'pushdown', 'row', 'pulldown', 'pull-up', 'lat ', 'shoulder', 'lateral', 'face pull', 'dip'].filter(s => allNames.includes(s)).length;
  const lowerSignals = ['squat', 'lunge', 'deadlift', 'rdl', 'hip thrust', 'leg press', 'leg curl', 'leg extension', 'calf', 'glute'].filter(s => allNames.includes(s)).length;
  const pushSignals = ['bench', 'press', 'fly', 'pushdown', 'tricep', 'dip', 'push-up', 'lateral raise'].filter(s => allNames.includes(s)).length;
  const pullSignals = ['row', 'pull', 'curl', 'lat ', 'chin', 'face pull', 'rear delt'].filter(s => allNames.includes(s)).length;
  
  const total = upperSignals + lowerSignals;
  if (total === 0) return null;
  
  // If mixed upper + lower → full body
  if (upperSignals > 0 && lowerSignals > 0 && Math.min(upperSignals, lowerSignals) / Math.max(upperSignals, lowerSignals) > 0.3) {
    return 'full';
  }
  
  // Predominantly lower
  if (lowerSignals > upperSignals) return 'lower';
  
  // Predominantly upper — check push vs pull
  if (pushSignals > pullSignals * 1.5) return 'push';
  if (pullSignals > pushSignals * 1.5) return 'pull';
  
  return 'upper'; // mixed upper
};

/**
 * Enhanced Template Selector Panel
 * - Search by name + tags + derived focus
 * - Focus filter pills with heuristic matching
 * - Role dots in exercise preview
 */
const TemplateSelectorPanel = ({ 
  templates = [], 
  onSelectTemplate, 
  onClose,
  isLoading = false 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [focusFilter, setFocusFilter] = useState('all');

  // Enrich templates with derived focus
  const enrichedTemplates = useMemo(() => {
    return templates.map(t => ({
      ...t,
      _derivedFocus: deriveTemplateFocus(t)
    }));
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return enrichedTemplates.filter(template => {
      const q = searchQuery.toLowerCase();
      const name = (template.name || template.template_name || '').toLowerCase();
      const tags = (template.tags || []).map(t => t.toLowerCase());
      const focus = (template.focus || '').toLowerCase();
      const derived = template._derivedFocus || '';

      const matchesSearch = !q ||
        name.includes(q) ||
        tags.some(t => t.includes(q)) ||
        focus.includes(q) ||
        derived.includes(q);

      const matchesFocus = focusFilter === 'all' ||
        focus === focusFilter ||
        focus.includes(focusFilter) ||
        derived === focusFilter ||
        tags.includes(focusFilter);

      return matchesSearch && matchesFocus;
    });
  }, [enrichedTemplates, searchQuery, focusFilter]);

  // Count per focus for pill badges
  const focusCounts = useMemo(() => {
    const counts = {};
    FOCUS_PILLS.forEach(f => { counts[f.value] = 0; });
    enrichedTemplates.forEach(t => {
      const d = t._derivedFocus;
      if (d && counts[d] !== undefined) counts[d]++;
    });
    counts['all'] = enrichedTemplates.length;
    return counts;
  }, [enrichedTemplates]);

  return (
    <div className="bg-blue-900/30 p-4 rounded-lg mb-6 border border-blue-500/20" data-testid="template-selector-panel">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-white font-medium flex items-center gap-2">
          <Dumbbell className="w-4 h-4 text-blue-400" />
          Select Template
        </h3>
        <button 
          onClick={onClose} 
          className="text-blue-300 hover:text-white p-1 rounded hover:bg-blue-800/50 transition"
          data-testid="close-template-selector"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Focus Pills */}
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {FOCUS_PILLS.map(f => (
          <button
            key={f.value}
            onClick={() => setFocusFilter(f.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
              focusFilter === f.value
                ? 'bg-blue-500 text-white'
                : 'bg-blue-900/40 text-blue-300 hover:bg-blue-800/50'
            }`}
            data-testid={`focus-pill-${f.value}`}
          >
            {f.label}
            <span className={`text-[10px] ${focusFilter === f.value ? 'text-blue-200' : 'text-blue-500'}`}>
              {focusCounts[f.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or tag..."
          className="w-full pl-10 pr-8 py-2 bg-blue-900/40 border border-blue-500/30 rounded-lg text-white placeholder-blue-400/60 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
          data-testid="template-search-input"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results Count */}
      <div className="text-blue-300 text-xs mb-3">
        {filteredTemplates.length} of {enrichedTemplates.length} templates
        {searchQuery && ` matching "${searchQuery}"`}
        {focusFilter !== 'all' && ` (${focusFilter})`}
      </div>

      {/* Template Grid */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-blue-300 mt-2">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-8">
          <Dumbbell className="w-12 h-12 text-blue-400/30 mx-auto mb-2" />
          <p className="text-blue-300">
            {enrichedTemplates.length === 0 ? 'No templates available' : 'No templates match your search'}
          </p>
          {(searchQuery || focusFilter !== 'all') && (
            <button
              onClick={() => { setSearchQuery(''); setFocusFilter('all'); }}
              className="mt-2 text-blue-400 hover:text-blue-300 text-sm underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-1">
          {filteredTemplates.map((template) => (
            <TemplateCard key={template.id} template={template} onSelect={() => onSelectTemplate(template)} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Template Card — shows name, tags, derived focus, exercise list with role dots
 */
const TemplateCard = ({ template, onSelect }) => {
  const templateName = template.name || template.template_name || 'Unnamed Template';
  const exercises = template.exercises || [];
  const tags = template.tags || [];
  const focus = template.focus || null;
  const derivedFocus = template._derivedFocus || null;
  const displayFocus = focus || derivedFocus;

  return (
    <div
      onClick={onSelect}
      className="p-3 bg-blue-800/30 hover:bg-blue-700/40 rounded-lg transition-all cursor-pointer border border-blue-500/20 hover:border-blue-400/40"
      data-testid={`template-card-${template.id}`}
    >
      {/* Name */}
      <div className="text-white font-medium mb-1 text-sm line-clamp-1">{templateName}</div>

      {/* Tags + Focus */}
      {(tags.length > 0 || displayFocus) && (
        <div className="flex gap-1 flex-wrap mb-1.5">
          {displayFocus && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${focus ? 'bg-blue-500/25 text-blue-200' : 'bg-gray-600/25 text-gray-300'}`}>
              {displayFocus}
            </span>
          )}
          {tags.filter(t => t !== focus).map((tag, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-600/30 text-gray-400">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Exercise Count */}
      <div className="text-blue-300 text-xs mb-1.5 flex items-center gap-1">
        <Dumbbell className="w-3 h-3" />
        {exercises.length} exercise{exercises.length !== 1 ? 's' : ''}
      </div>

      {/* Exercise List with Role Dots */}
      {exercises.length > 0 && (
        <div className="border-t border-blue-500/20 pt-1.5 mt-1.5">
          <ul className="space-y-0.5">
            {exercises.map((ex, idx) => (
              <li key={idx} className="text-[11px] text-blue-200/80 flex items-center gap-1.5 leading-tight">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ROLE_DOT[ex.role] || 'bg-gray-600'}`} />
                <span className="truncate flex-1">
                  {ex.exercise_name || ex.name || 'Unknown'}
                </span>
                <span className="text-blue-400/50 text-[10px] flex-shrink-0 font-mono">
                  {ex.sets || ex.target_sets || '?'}x{ex.reps || ex.target_reps || '?'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TemplateSelectorPanel;
